import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  AUTH_PROVIDER_KEY,
  AZURE_SSO_SESSION_KEY,
  MICROSOFT_SESSION_USER_KEY,
  MS_LOGIN_EMAIL_KEY,
  MS_LOGIN_PENDING_KEY,
} from "../auth/authSessionKeys";
import { isMicrosoftSession, logoutMicrosoft } from "../auth/microsoftAuth";
import MuiTooltipWrapper from "../component/Tooltip/Tooltip";
import { backend_url } from "../services/api";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { getSupportStaffRoleLabel } from "./supportStaffRoles";

export const baseUrlLink = `${backend_url}/api`;

export const isGridPolaris = true
  // ["thisisme", "thisisme1"].includes(hostname?.split(".")[0]?.split("-")[0]) ? false : true;
export const useMentorRoleDisplay = (userRole) => {
  const userInfo = useSelector((state) => state.userInfo);
  const { role } = userInfo?.data || {};
  return userRole ?  userRole  : role === "mentor" ? MentorType : EventManagerType;
};
export const useMenteeRoleDisplay = (userRole) => {
  const userInfo = useSelector((state) => state.userInfo);
  const { role } = userInfo?.data || {};
  return userRole ?  userRole  : role === "mentee" ? MenteeType : VolunteerType;
};
// Determine current theme
export const currentTheme = localStorage.getItem("theme") || "mma";
export const isVMSTheme = currentTheme === "vms";
export const isTIMTheme = currentTheme === "tim";
export const isBPSTheme = currentTheme === "bps";
export const USER_TYPES = {
  VOLUNTEER: "volunteer",
  EVENT_MANAGER: "event_manager",
  HTA: "hta",
  MENTOR: "mentor",
  MENTEE: "mentee",
  ADMIN: "admin",
};
export function getVolunteerMenteeRoles(role) {
  if (!role) return false;
  const isMenteeVolunteerRole = [
    USER_TYPES.VOLUNTEER,
    USER_TYPES.MENTEE,
  ].includes(role);
  return isMenteeVolunteerRole;
}
export function getEventMangerMentorRoles(role) {
  if (!role) return false;
  const isEventManagerMentor = [
    USER_TYPES.EVENT_MANAGER,
    USER_TYPES.MENTOR,
  ].includes(role);
  return isEventManagerMentor;
}
export function getAdminRoles(role) {
  if (!role) return false;
  const isAdmin = [USER_TYPES.ADMIN].includes(role);
  return isAdmin;
}

export const getDisplayLabel = (label, assistanceType, registrationStatus) => {
  if (!label) return "";

  const normalized = String(label).toLowerCase();
  const normalizedAssistanceType = String(assistanceType || "").toLowerCase();
  const normalizedRegistrationStatus = String(registrationStatus || "").toLowerCase();

  if(normalized === "admin") return "Supervisor";
  if (normalized === "hta") return "HTA";
  if (normalized === "medical_assistant") {
    if (normalizedAssistanceType === "dental" || normalizedAssistanceType === "dental_assistant") {
      if (
        normalizedRegistrationStatus === "non_registered" ||
        normalizedRegistrationStatus === "nonregistered"
      ) {
        return "Dental Assistant (Non RDA)";
      }
      if (normalizedRegistrationStatus === "registered") {
        return "Dental Assistant (RDA)";
      }
      return "Dental Assistant";
    }
    if (normalizedAssistanceType === "medical" || normalizedAssistanceType === "medical_assistant") {
      return "Medical Assistant";
    }
    return "Assistance";
  }
  if (normalized === "provider") return "Provider";
  if (normalized === "support_staff") return "Support Staff";

  const supportStaffRoleLabel = getSupportStaffRoleLabel(normalized);
  if (supportStaffRoleLabel && supportStaffRoleLabel !== normalized) {
    return supportStaffRoleLabel;
  }

  return String(label).replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export const MEMBER_USER_TABS = [
  { key: "all_users", label: "Admins" },
  { key: "provider", label: "Providers" },
  { key: "medical_assistant", label: "Medical Assistants" },
  { key: "dental_assistant", label: "Dental Assistants" },
  { key: "hta", label: "HTAs" },
  { key: "support_staff", label: "Support Staff" },
  { key: "payroll_supervisor", label: "Workforce Supervisors" },
];

export const memberTabKeyToUrlParam = (tabKey) =>
  tabKey === "all_users" ? "admin" : tabKey;

export const memberUrlParamToTabKey = (tabParam) => {
  if (!tabParam || tabParam === "admin") return "all_users";
  return MEMBER_USER_TABS.some((tab) => tab.key === tabParam)
    ? tabParam
    : "all_users";
};

export const memberRoleToTabKey = (role) => {
  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") return "all_users";
  if (
    normalizedRole === "medical_assistant" ||
    normalizedRole === "dental_assistant"
  ) {
    return "medical_assistant";
  }
  return MEMBER_USER_TABS.some((tab) => tab.key === normalizedRole)
    ? normalizedRole
    : "all_users";
};

export const resolveMemberTabKey = (role, preferredTab) => {
  const normalizedPreferred = (preferredTab || "").toLowerCase();
  if (MEMBER_USER_TABS.some((tab) => tab.key === normalizedPreferred)) {
    return normalizedPreferred;
  }
  return memberRoleToTabKey(role);
};

export const getMemberUserTabLabel = (role, membersTab) => {
  const normalizedRole = (role || "").toLowerCase();
  if (
    normalizedRole === "medical_assistant" ||
    normalizedRole === "dental_assistant"
  ) {
    if (membersTab === "medical_assistant") {
      return "Medical Assistant";
    }
    if (membersTab === "dental_assistant") {
      return "Dental Assistant";
    }
    return "Assistance";
  }
  const tabKey = memberRoleToTabKey(normalizedRole);
  return (
    MEMBER_USER_TABS.find((tab) => tab.key === tabKey)?.label || "Users"
  );
};

// Centralized role mapping configuration
export const ROLE_MAPPINGS = {
  vms: {
    mentee: "Mentee",
    mentor: "Mentor",
    admin: "Admin",
    super_admin: "Admin",
    event_manager: "Supervisor",
    volunteer: "Volunteer",
    users: "Users",
  },
  tim: {
    mentee: "Mentee",
    mentor: "Mentor",
    admin: "Admin",
    super_admin: "Admin",
    event_manager: "Supervisor",
    volunteer: "Volunteer",
    users: "Users",
    make_a_request: "Make a Request",
    volunteer_photographer: "Volunteer Photographer",
  },
  mma: {
    mentee: "Mentee",
    mentor: "Mentor",
    admin: "Admin",
    super_admin: "Super Admin",
    event_manager: "Event Manager",
    volunteer: "Volunteer",
    users: "Users",
  },
  lca: {
    mentee: "Mentee",
    mentor: "Mentor",
    admin: "Admin",
    super_admin: "Super Admin",
    event_manager: "Event Manager",
    volunteer: "Volunteer",
    users: "Users",
  },
  bps: {
    mentee: "Mentee",
    mentor: "Mentor",
    admin: "Admin",
    super_admin: "Admin",
    event_manager: "Supervisor",
    volunteer: "Volunteer",
    users: "Users",
  },
};

// Get role display name based on current theme
export function getRoleDisplayName(role) {
  if (!role) return "";
  
  let  roleKey = role.toLowerCase();
  const themeRoles = ROLE_MAPPINGS[currentTheme];
  return themeRoles?.[roleKey] || role;
}

// Get plural form of role
export function getRoleDisplayNamePlural(role) {
  const singular = getRoleDisplayName(role);

  // Handle special cases
  if (
    singular === "Admin" ||
    singular === "Super Admin" ||
    singular === "Supervisor"
  ) {
    return singular + "s";
  }

  // Default pluralization
  return singular + "s";
}

// Commonly used role display names (for backward compatibility)
export const MentorType = getRoleDisplayName("mentor");
export const MenteeType = getRoleDisplayName("mentee");
export const AdminType = getRoleDisplayName("admin");
export const SuperAdminType = getRoleDisplayName("super_admin");
export const EventManagerType = getRoleDisplayName("event_manager");
export const VolunteerType = getRoleDisplayName("volunteer");
export const Users = getRoleDisplayName("users");

// Plural versions
// Updated plural versions with TIM theme handling
export const MentorsType = getRoleDisplayNamePlural(
  isGridPolaris || currentTheme === "tim" ? "mentor" : "event_manager"
);
export const MenteesType = getRoleDisplayNamePlural(
  isGridPolaris || currentTheme === "tim" ? "mentee" : "volunteer"
);
export const EventManagersType = getRoleDisplayNamePlural("event_manager");
export const VolunteersType = getRoleDisplayNamePlural("volunteer");

// Item name configuration (Program vs Event)
export const ITEM_MAPPINGS = {
  vms: {
    singular: "event",
    plural: "events",
    title: "Event",
    titlePlural: "Events",
  },
  default: {
    singular: "event",
    plural: "events",
    title: "Event",
    titlePlural: "Events",
  },
  tim: {
    singular: "event",
    plural: "events",
    title: "Event",
    titlePlural: "Events",
  },
  bps: {
    singular: "event",
    plural: "events",
    title: "Event",
    titlePlural: "Events",
  },
};

export const itemName =
  ITEM_MAPPINGS[currentTheme]?.singular || ITEM_MAPPINGS.default.singular;
export const itemNamePlural =
  ITEM_MAPPINGS[currentTheme]?.plural || ITEM_MAPPINGS.default.plural;
export const itemTitle =
  ITEM_MAPPINGS[currentTheme]?.title || ITEM_MAPPINGS.default.title;
export const itemTitlePlural =
  ITEM_MAPPINGS[currentTheme]?.titlePlural || ITEM_MAPPINGS.default.titlePlural;

// Action modal configurations
export const ACTION_MODAL_TRAINING = {
  assign_mentees: MenteeType,
  assign_mentors: MentorType,
  assign_providers: "Provider",
  assign_medical_assistants: "Medical Assistant",
  assign_hta: "Healthcare Transportation Associate",
  assign_supervisors: "Supervisor",
};

export const ACTION_MODAL = {
  assign_mentees: "Volunteer",
  assign_mentors: "Supervisor",
  assign_providers: "Provider",
  assign_medical_assistants: "Medical Assistant",
  assign_hta: "Healthcare Transportation Associate",
  assign_supervisors: "Supervisor",
};

export const ASSIGN_MEMBER_FIELDS = [
  "assign_providers",
  "assign_medical_assistants",
  "assign_hta",
  "assign_supervisors",
];

// Utility function to get all roles for current theme
export function getAllRolesForTheme() {
  return ROLE_MAPPINGS[currentTheme] || ROLE_MAPPINGS.mma;
}

// Utility function to get role key from display name
export function getRoleKeyFromDisplayName(displayName) {
  const themeRoles = ROLE_MAPPINGS[currentTheme];

  for (const [key, value] of Object.entries(themeRoles)) {
    if (value === displayName) {
      return key;
    }
  }

  return displayName.toLowerCase();
}

export function getUserInfo() {
  try {
    const token = localStorage.getItem("access_token");
    // const token = decryptToken(localStorage.getItem('access_token'));
    const { user_id, roles, email, first_name, last_name } = jwtDecode(token);
    return { user_id, roles, email, first_name, last_name };
  } catch {
    return null;
  }
}

// Legacy support - keeping the original roleLabel structure
export const roleLabel = ROLE_MAPPINGS;
export const StepsList = [
  {
    name: "Personal Information",
    status: "In-Progress",
    key: "personal_information",
  },
  {
    name: "Skills and Areas of Expertise",
    status: "Waiting",
    key: "area_of_expertise",
  },
  {
    name: "Educational Background",
    status: "Waiting",
    key: "educational_background",
  },
  {
    name: "Mentorship Experience",
    status: "Waiting",
    key: "mentor_ship_experience",
  },
  {
    name: "Goals",
    status: "Waiting",
    key: "goals_expectations",
  },
  {
    name: "Documents Upload",
    status: "Waiting",
    key: "document_upload",
  },
];

export const MenteeStepsList = [
  {
    name: "Personal Information",
    status: "In-Progress",
    key: "personal_information",
  },
  {
    name: "Current Status",
    status: "Waiting",
    key: "current_status",
  },
  {
    name: "Skills and Interests",
    status: "Waiting",
    key: "skills_and_interests",
  },
  {
    name: "Goals",
    status: "Waiting",
    key: "expectations_goals",
  },
  {
    name: "Long-term Vision",
    status: "Waiting",
    key: "long_term_vision",
  },
  {
    name: "Documents Upload",
    status: "Waiting",
    key: "document_upload",
  },
];

export const userStatus = {
  load: "loaded",
  create: "created",
  pending: "pending",
  login: "loggedin",
  token: "tokenfetched",
  getToken: "getToken",
  role: "roleUpdated",
  otp: "otpTriggered",
  otpSuccess: "otpVerified",
  changePassword: "passwordUpdated",
  questions: "questionsUpdated",
  categoryPrograms: "categoryprogramloaded",
};

export const programStatus = {
  load: "loaded",
  create: "programcreated",
  update: "programupdated",
  exist: "alreadyexist",
  error: "error",
  curated: "curated",
  yetToPlan: "yettoplan",
  bookmarked: "bookmarked",
  planned: "planned",
  assigned: "assigned",
  inProgress: "inprogress",
  start: "start",
  paused: "paused",
  completed: "completed",
  taskassigned: "taskassigned",
  taskstarted: "taskstarted",
  tasksubmitted: "tasksubmitted",
};

export const goalStatus = {
  load: "loaded",
  request: "request",
  create: "created",
  statusupdate: "statusupdate",
  active: "active",
  start: "start",
  abort: "abort",
  complete: "complete",
  update: "updated",
  delete: "deleted",
};

export const feedStatus = {
  load: "loaded",
  create: "created",
  update: "updated",
  delete: "deleted",
  createcomment: "createdcomment",
  postlike: "postlike",
};

export const reportsStatus = {
  load: "loaded",
  create: "created",
  update: "updated",
  delete: "deleted",
};
export const certificateStatus = {
  load: "loaded",
  create: "created",
  update: "updated",
  download: "download",
  delete: "deleted",
};

export const calendarStatus = {
  load: "loaded",
  create: "created",
};

export const requestStatus = {
  load: "loaded",
  programupdate: "programupdated",
  certificateupdate: "certificateupdated",
  goalupdate: "goalupdated",
  categoryload: "categoryloaded",
  memberload: "memberloaded",
  memberupdate: "memberupdated",
  membercancel: "membercancelled",
  reschedule: "rescheduled",
  reportupdate: "reportupdated",
  cancel: "cancelled",
  autoapproval: "autoapproval",
  testimonialupdate: "testimonialupdated",
};

export const profileStatus = {
  load: "loaded",
  update: "updated",
  image: "imageupdated",
};

export const goalPeriods = [
  {
    name: "1 Month",
    value: 1,
  },
  {
    name: "3 Months",
    value: 3,
  },
  {
    name: "6 Months",
    value: 6,
  },
  {
    name: "12 Months",
    value: 12,
  },
];
export const StatusbuttonStyles = {
  base: {
    borderRadius: "5px",
    padding: "12px 64px",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
  },
  gradient: {
    background: "linear-gradient(94.18deg, #00AEBD -38.75%, #1D5BBF 195.51%)",
  },
  success: {
    background: "#16B681",
  },
  danger: {
    border: "1px solid #E0382D",
    color: "#E0382D",
  },
};

export const wcagColor = {
  wcagColorGreen: {
    bgColor: "rgba(209, 250, 229, 1)", // emerald-100
    color: "rgba(4, 120, 87, 1)", // emerald-700
  },
  wcagColorYellow: {
    bgColor: "rgba(254, 243, 199, 1)", // amber-100
    color: "rgba(180, 83, 9, 1)", // amber-700
  },
  wcagColorRed: {
    bgColor: "rgba(254, 226, 226, 1)", // red-100
    color: "rgba(185, 28, 28, 1)", // red-700
  },
  wcagColorBlue: {
    bgColor: "rgba(238, 245, 255, 1)", // blue-100
    color: "rgba(29, 91, 191, 1)", // blue-700
  },
  wcagColorGray: {
    bgColor: "rgba(223, 223, 223, 1)", // gray-200
    color: "rgba(79, 79, 79, 1)", // gray-700
  },
  wcagColorOrange: {
    bgColor: "rgba(255, 242, 231, 1)", // orange-100
    color: "rgba(255, 118, 0, 1)", // orange-700
  },
};

export const activityStatusColor = {
  create: "rgba(29, 91, 191, 1)",
  active: "rgba(18, 179, 71, 1)",
  delete: "rgba(224, 56, 45, 1)",
  complete: "rgba(0, 174, 189, 1)",
  ongoing: "rgba(255, 212, 27, 1)",
  abort: "rgba(255, 0, 215, 1)",
  update: "rgba(255, 118, 0, 1)",
  approve: "rgba(0, 174, 189, 1)",
  inreview: "rgba(255, 212, 27, 1)",
};

export const goalDataStatus = {
  inactive: "Not Active Goal",
  create: "Waiting for Approval",
  pending: "Waiting for Approval",
  active: "Active",
  ongoing: "Ongoing",
  completed: "Completed",
  aborted: "Aborted",
  new: "New",
  cancel: "Cancel",
};

export const goalStatusColor = {
  inactive: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  create: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  pending: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  active: {
    color: "rgba(255, 212, 27, 1)",
    bg: "rgba(255, 247, 216, 1)",
  },
  ongoing: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  completed: {
    color: "rgba(47, 147, 132, 1)",
    bg: "rgba(218, 252, 231, 1)",
  },
  aborted: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  cancel: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
};

export const goalRequestStatus = {
  inactive: "Inactive",
  active: "Active",
  new: "New",
  pending: "Pending",
  accept: "Accept",
  decline: "Decline",
  cancel: "Cancel",
  in_progress: "In Progress",
  completed: "Completed",
};
export const goalHeadingStatus = {
  inactive: "Inactive Goal",
  active: "Active Goal",
  new: "New Goal",
  pending: "Pending Goal",
  accept: "Accept Goal",
  decline: "Declined Goal",
  cancel: "Cancelled Goal",
  in_progress: "Goal In Progress",
  completed: "Completed Goal",
};

export const goalRequestColor = {
  inactive: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  new: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(238, 245, 255, 1)",
  },
  create: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(238, 245, 255, 1)",
  },
  active: {
    color: "rgba(255, 212, 27, 1)",
    bg: "rgba(255, 247, 216, 1)",
  },
  accept: {
    color: "rgba(22, 182, 129, 1)",
    bg: "rgba(235, 255, 243, 1)",
  },
  pending: {
    color: "rgba(255, 213, 0, 1)",
    bg: "rgba(255, 251, 233, 1)",
  },
  decline: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  cancel: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  in_progress: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  completed: {
    color: "rgba(47, 147, 132, 1)",
    bg: "rgba(218, 252, 231, 1)",
  },
};

export const eventColors = {
  all: "#00AEBD",
  upcoming: "#1D5BBF",
  reschedule: "#FF8A00",
  completed: "#16B681",
  cancelled: "#E0382D",
  draft: "#B7B7B7",
};

export const certificateText = {
  inprogress: "In-Progress",
  yettoapprove: "Yet to Approve",
  yettojoin: "Yet to Join",
  yettostart: "Yet to Start",
  assigned: "Assigned",
  paused: "Paused",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const resultText = {
  Pass: "Pass",
  Fail: "No Pass",
};

export const certificateResultText = {
  pass: "Pass",
  fail: "Fail",
};

export const empty = [
  {
    title: "",
    value: 100,
    color: "rgba(0, 174, 189, 1)",
  },
];
export const resultColor = {
  Pass: {
    color: "rgba(47, 147, 132, 1)",
    bg: "rgba(218, 252, 231, 1)",
  },
  Fail: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
};

export const certificateResultColor = {
  pass: {
    color: "rgba(47, 147, 132, 1)",
    bg: "rgba(218, 252, 231, 1)",
  },
  fail: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
};
export const certificateColor = {
  yettoapprove: {
    color: "#000",
    bg: "#fff",
  },
  yettostart: {
    color: "#000",
    bg: "#fff",
  },
  assigned: {
    color: "#000",
    bg: "#fff",
  },
  yettojoin: {
    color: "rgba(255, 212, 27, 1)",
    bg: "rgba(255, 247, 216, 1)",
  },
  inprogress: {
    color: "rgba(255, 118, 0, 1)",
    bg: "rgba(255, 242, 231, 1)",
  },
  completed: {
    color: "rgba(47, 147, 132, 1)",
    bg: "rgba(218, 252, 231, 1)",
  },
  paused: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  cancelled: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
};

export const requestStatusText = {
  new: "New",
  pending: "Pending",
  accept: "Accept",
  launched: "Launched",
  cancel: "Cancel",
  Wating_for_response: "Wating for Response",
  active: "Active",
  deactivated: "Deactivated",
  rejected: "Rejected",
  approved: "Approved",
  in_progress: "In Progress",
  cancelled: "Cancelled",
  inreview: "In-Review",
  deactive: "Deactive",
};
export const requestStatusTextForFollowRequest = {
  new: "New",
  pending: "Pending",
  accept: "Connect",
  launched: "Launched",
  cancel: "Reject",
  Wating_for_response: "Wating for Response",
  active: "Active",
  deactivated: "Deactivated",
  rejected: "Rejected",
  approved: "Approved",
  in_progress: "In Progress",
  cancelled: "Cancelled",
};
export const certificateRequestStatusText = {
  new: "New",
  pending: "Pending",
  accept: "Accept",
  launched: "Launched",
  cancel: "Cancel",
  Waiting_for_response: "Waiting for your Response",
  active: "Active",
  deactivated: "Deactivated",
  approved: "Approved",
  rejected: "Rejected",
};
export const programStatusText = {
  inprogress: "Ongoing",
  started: "Ongoing",
  completed: "Completed",
  cancelled: "Cancelled",
  new_program_request_rejected: "Rejected",
  yettojoin: "Yet to Launch",
  yettostart: "Yet to Start",
  draft: "Draft",
  yettoapprove: "Waiting for Approval",
};

export const programStatusColor = {
  yettojoin: {
    bgColor: "rgba(238, 245, 255, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  yettostart: {
    bgColor: "rgba(255, 251, 233, 1)",
    color: "rgba(255, 213, 0, 1)",
  },
  completed: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "#33bc93",
  },
  cancelled: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(224, 56, 45, 1)",
  },

  draft: {
    bgColor: "rgba(207, 225, 255, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  yettoapprove: {
    bgColor: "rgba(255, 251, 233, 1)",
    color: "rgba(255, 213, 0, 1)",
  },
  new_program_request_rejected: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(224, 56, 45, 1)",
  },
  inprogress: {
    bgColor: "#ffead1",
    color: "#ffb155",
  },
  started: {
    bgColor: "#ffead1",
    color: "#ffb155",
  },
  assign_program_accepted: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "rgba(22, 182, 129, 1)",
  },
};

export const requestStatusColor = {
  new: {
    bgColor: "rgba(238, 245, 255, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  pending: {
    bgColor: "#FFF8D6",
    color: "#B78100",
  },
  waiting_for_response: {
    bgColor: "rgba(255, 251, 233, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  accept: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "rgba(22, 182, 129, 1)",
  },
  launched: {
    bgColor: wcagColor.wcagColorGreen.bgColor,
    color: wcagColor.wcagColorGreen.color,
  },
  cancel: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(224, 56, 45, 1)",
  },
  active: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "rgba(22, 182, 129, 1)",
  },
  deactivated: {
    bgColor: "rgba(207, 225, 255, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  review: {
    bgColor: "rgba(207, 225, 255, 1)",
    color: "rgba(29, 91, 191, 1)",
  },
  rejected: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(224, 56, 45, 1)",
  },
  approved: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "rgba(22, 182, 129, 1)",
  },
  in_progress: {
    bgColor: "rgba(255, 242, 231, 1)",
    color: "rgba(255, 118, 0, 1)",
  },
  inreview: {
    bgColor: "rgba(255, 242, 231, 1)",
    color: "rgba(255, 118, 0, 1)",
  },
  cancelled: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(224, 56, 45, 1)",
  },
};

export const memberStatusColor = {
  accept: {
    bgColor: "rgba(235, 255, 243, 1)",
    color: "rgba(0, 128, 0, 1)",
  },
  cancel: {
    bgColor: "rgba(255, 231, 231, 1)",
    color: "rgba(192, 0, 0, 1)",
  },
};

export const taskStatusText = {
  draft: "Draft",
  inprogress: "In-Progress",
  rejected: "Cancel",
  pending: "Pending",
  newtask: "New",
  waiting_for_approval: "Waiting",
  completed: "Completed",
  reassigned: "Re Assigned",
};
export const taskStatusTextMentee = {
  draft: "Draft",
  inprogress: "In-Progress",
  rejected: "Cancel",
  pending: "Pending",
  newtask: "New",
  waiting_for_approval: `${
    isVMSTheme
      ? "Waiting for Event manager approval"
      : "Waiting for mentor approval"
  }`,
  completed: "Completed",
  reassigned: "Re Assigned",
};

export const ProgramHeaderTextMentee = {
  draft: `Draft ${itemTitlePlural}`,
  inprogress: `Ongoing ${itemTitlePlural}`,
  rejected: `Cancelled ${itemTitlePlural}`,
  completed: `Completed ${itemTitlePlural}`,
  reassigned: `Re Assigned`,
  yettojoin: `My ${itemTitlePlural}`,
  planned: `My ${itemTitlePlural}`,
  yettostart: `Recently Joined ${itemTitlePlural}`,
  reschedule: `Rescheduled ${itemTitlePlural}`,
};

export const ticketStatusText = {
  all: "All",
  new: "New",
  pending: "Pending",
  inprogress: "In_Progress",
  closed: "Closed",
  reject: "Reject",
};

// STATUS_CHOICES = [
//   ('new', 'New'),
//   ('in_progress', 'In Progress'),
//   ('closed', 'Closed'),
//   ('rejected', 'Rejected'),
//   ('pending', 'Pending'),
//   ('reopened', 'Reopened'),
// ];

export const TicketStatusColor = {
  all: {
    color: "#8B6B00",
    bg: "#FFF8E1",
  },
  rejected: {
    color: "#B71C1C",
    bg: "#FFEBEE",
  },
  pending: {
    color: "#B78100",
    bg: "#FFF8E1",
  },
  in_progress: {
    color: "#B75D00",
    bg: "#FFF3E0",
  },
  closed: {
    color: "#B78100",
    bg: "#FFF8E1",
  },
  new: {
    color: "#0D47A1",
    bg: "#E3F2FD",
  },
  resolved: {
    color: "#1B5E20",
    bg: "#E8F5E9",
  },
};

export const taskStatusColor = {
  draft: {
    color: "black",
    bg: "#c6c6c6",
  },
  rejected: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  pending: {
    color: "rgba(255, 213, 0, 1)",
    bg: "rgba(255, 251, 233, 1)",
  },
  inprogress: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(207, 225, 255, 1)",
  },
  waiting_for_approval: {
    color: "rgba(29, 91, 191, 1)",
    bg: "",
  },
  newtask: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(207, 225, 255, 1)",
  },
  completed: {
    color: "rgba(22, 182, 129, 1)",
    bg: "rgba(235, 255, 243, 1)",
  },
  reassigned: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(207, 225, 255, 1)",
  },
};

export const reportStatusColor = {
  draft: {
    color: "rgba(224, 56, 45, 1)",
    bg: "#fff",
  },
  new: {
    color: "rgba(29, 91, 191, 1)",
    bg: "rgba(238, 245, 255, 1)",
  },
  pending: {
    color: "rgba(255, 213, 0, 1)",
    bg: "rgba(255, 251, 233, 1)",
  },
  accept: {
    color: "rgba(22, 182, 129, 1)",
    bg: "rgba(235, 255, 243, 1)",
  },
  cancel: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  deleted: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  rejected: {
    color: "rgba(224, 56, 45, 1)",
    bg: "rgba(255, 231, 231, 1)",
  },
  approved: {
    color: "rgba(22, 182, 129, 1)",
    bg: "rgba(235, 255, 243, 1)",
  },
};

export const reportStatus = {
  pending: "Pending",
  accept: "Accept",
  cancel: "Cancel",
  draft: "Draft",
  new: "New",
  deleted: "Deleted",
  rejected: "Rejected",
  approved: "Approved",
};

export const reportAllStatus = {
  pending: "pending",
  accept: "accept",
  cancel: "cancel",
  draft: "draft",
  new: "new",
};

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 20;

export const PasswordRulesSet = {
  character: "character",
  upperlowercase: "upperlowercase",
  number: "number",
  email: "email",
  common: "common",
};

export const statusAction = [
  "yettoapprove",
  "yettojoin",
  "yettostart",
  "inprogress",
  "completed",
  "cancelled",
  "is_bookmark",
  "draft",
  "reschedule",
  "assign_program",
  "interest",
  "upcoming",
];

export const programActionStatus = {
  all: "all",
  yettoapprove: "yettoapprove",
  yettojoin: "yettojoin",
  yettostart: "yettostart",
  yettoassign: "yettoassign",
  assigned: "assigned",
  paused: "paused",
  inprogress: "inprogress",
  completed: "completed",
  started: "started",
  cancelled: "cancelled",
  learning: "learning",
  bookmark: "is_bookmark",
  planned: "planned",
  draft: "draft",
  reschedule: "reschedule",
  program_join_request_accepted: "program_join_request_accepted",
  program_assign: "assign_program",
  recent_join: "recently_joined",
  ongoing: "ongoing",
  interest: "interest",
  upcoming: "upcoming",
  archived: "archived",
};

export const pipeUrls = {
  login: "/login",
  register: "/register",
  dashboard: "/dashboard",
  programs: "/programs",
  programdetails: "/program-details",
  assigntask: "/assign-task",
  assignmentess: "/assign-mentees",
  createprogram: "/create-program",
  startprogram: "/start-program",
  reports: "/reports",
  mentortask: "/mentor-tasks",
};

export const programFilterUrls = (is_event = false) => {
  return {
    yettoapprove: is_event
      ? `?type=${programActionStatus.yettoapprove}&is_event=true`
      : `?type=${programActionStatus.yettoapprove}`,
    yettojoin: is_event
      ? `?type=${programActionStatus.yettojoin}&is_event=true`
      : `?type=${programActionStatus.yettojoin}`,
    recent_join: is_event
      ? `?type=${programActionStatus.recent_join}&is_event=true`
      : `?type=${programActionStatus.recent_join}`,
    ongoing: is_event
      ? `?type=${programActionStatus.ongoing}&is_event=true`
      : `?type=${programActionStatus.ongoing}`,
    planned: is_event
      ? `?type=${programActionStatus.planned}&is_event=true`
      : `?type=${programActionStatus.planned}`,
    yettostart: is_event
      ? `?type=${programActionStatus.yettostart}&is_event=true`
      : `?type=${programActionStatus.yettostart}`,
    inprogress: is_event
      ? `?type=${programActionStatus.inprogress}&is_event=true`
      : `?type=${programActionStatus.inprogress}`,
    completed: is_event
      ? `?type=${programActionStatus.completed}&is_event=true`
      : `?type=${programActionStatus.completed}`,
    reschedule: is_event
      ? `?type=${programActionStatus.reschedule}&is_event=true`
      : `?type=${programActionStatus.reschedule}`,
    cancelled: is_event
      ? `?type=${programActionStatus.cancelled}&is_event=true`
      : `?type=${programActionStatus.cancelled}`,
    learning: is_event
      ? `?type=${programActionStatus.learning}&is_event=true`
      : `?type=${programActionStatus.learning}`,
    bookmark: is_event
      ? `?type=${programActionStatus.bookmark}&is_event=true`
      : `?type=${programActionStatus.bookmark}`,
    draft: is_event
      ? `?type=${programActionStatus.draft}&is_event=true`
      : `?type=${programActionStatus.draft}`,
    programAssign: is_event
      ? `?type=${programActionStatus.program_assign}&is_event=true`
      : `?type=${programActionStatus.program_assign}`,
    interest: is_event
      ? `?type=${programActionStatus.interest}&is_event=true`
      : `?type=${programActionStatus.interest}`,
    upcoming: is_event
      ? `?type=${programActionStatus.upcoming}&is_event=true`
      : `?type=${programActionStatus.upcoming}`,
    archived: is_event
      ? `?type=${programActionStatus.archived}&is_event=true&is_active=false`
      : `?type=${programActionStatus.archived}&is_active=false`,
  };
};

export const menteeCountStatus = {
  all: "allprogram",
  [programActionStatus.inprogress]: "ongoing",
  [programActionStatus.completed]: "completed",
  [programActionStatus.yettojoin]: "curated",
  [programActionStatus.learning]: "mylearning",
  [programActionStatus.bookmark]: "bookmark",
  [programActionStatus.planned]: "planned",
};

export const getChartColors = () => {
  const primaryColor = selectedTheme === tenants.VMS ? "#FF7700" : "#D4E6FF";
  const secondaryColor = selectedTheme === tenants.VMS ? "#FFBF6C" : "#1D5BBF";
  const otherColor = selectedTheme === tenants.VMS ? "#656565" : "#FFBB00";
  return { primaryColor, secondaryColor, otherColor };
};

export const getCategoryColor = () => [
  "#FDC0A5",
  "#DD8B5D",
  "#B85424",
  "#EADA8D",
  "#51908E",
  "#415862",
  "#FDF3CA",
  "#82DDC1",
  "#4284F1",
  "#34E4FA",
  "#9CA3AE",
  "#C50037",
  "#FA6054",
  "#319FBA",
];

export const programMenus = (
  page = "dashboard",
  role = "admin",
  is_event = false
) => {
  const pipeUrl =  page === "program"
    ? pipeUrls.programs
    : pipeUrls.dashboard;

  const getMyProgramTooltip = (kind) => {
    const label = is_event ? "Events" : `${itemTitlePlural}`;
    const ongoingLabel = is_event ? "events" : `${itemTitlePlural}`.toLowerCase();

    switch (role) {
      case "admin":
        return kind === "ongoing"
          ? `Ongoing ${ongoingLabel}`
          : `${label} that are scheduled to start soon`;
      case "hta":
        return kind === "ongoing"
          ? `Ongoing ${ongoingLabel} that are assigned`
          : `${label} that are assigned and scheduled to start soon`;
      default:
        return "";
    }
  };
  return [
    {
      name: (
        <MuiTooltipWrapper
          title={`Total number of ${
            is_event ? "events" : `${itemName}`
          } created in the system`}
        >
          {`Total ${is_event ? "Events" : `${itemTitlePlural}`}`}
        </MuiTooltipWrapper>
      ),
      count: 0,
      page: pipeUrls.programs.concat(is_event ? "?is_event=true" : ""),
      for: ["admin", "hta"],
      mentorStatus: "allprogram",
      menteeStatus: "allprogram",
      adminStatus: "allprogram",
      status: "allprogram",
    },
    {
      name: (
        <MuiTooltipWrapper title={getMyProgramTooltip("upcoming")}>
          Upcoming Events
        </MuiTooltipWrapper>
      ),

      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).yettostart}`,
      menteePage: `${pipeUrl}${programFilterUrls(is_event).yettostart}`,
      for: ["admin", "hta",],
      mentorStatus: programActionStatus.yettostart,
      adminStatus: programActionStatus.yettostart,
      menteeStatus: "yettostart",
      status: programActionStatus.yettostart,
    },
    {
      name: (
        <MuiTooltipWrapper title={getMyProgramTooltip("ongoing")}>
          Ongoing Events
        </MuiTooltipWrapper>
      ),

      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).inprogress}`,
      for: ["admin", "hta"],
      mentorStatus: programActionStatus.inprogress,
      adminStatus: programActionStatus.inprogress,
      menteeStatus: "inprogress",
      status: programActionStatus.inprogress,
    },
    

    // {
    //   name: "Recently Joined Programs",
    //   count: 0,
    //   page: `${pipeUrl}${programFilterUrls.yettostart}`,
    //   menteePage: `${pipeUrl}${programFilterUrls.recent_join}`,
    //   for: ["mentor", "admin", "mentee"],
    //   mentorStatus: programActionStatus.yettostart,
    //   adminStatus: programActionStatus.yettostart,
    //   menteeStatus: "recently_joined",
    //   status: programActionStatus.yettostart,
    // },

  
    {
      name: (
        <MuiTooltipWrapper
          title={`${
            is_event ? "Events" : `${itemTitlePlural}`
          } that are drafted but not yet published`}
        >
          {`Draft ${is_event ? "Events" : `${itemTitlePlural}`}`}
        </MuiTooltipWrapper>
      ),
      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).draft}`,
      for: ["admin"],
      mentorStatus: "draft",
      menteeStatus: "",
      status: programActionStatus.draft,
    },
    // {
    //   name: "Upcoming Programs",
    //   count: 0,
    //   page: `${pipeUrl}${programFilterUrls.upcoming}`,
    //   for: ["mentee"],
    //   mentorStatus: "",
    //   menteeStatus: "upcoming",
    //   status: programActionStatus.upcoming,
    // },

    {
      name: (
        <MuiTooltipWrapper
          title={`${
            is_event ? "Events" : `${itemTitlePlural}`
          } that were successfully completed`}
        >
          {`Completed ${is_event ? "Events" : `${itemTitlePlural}`}`}
        </MuiTooltipWrapper>
      ),
      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).completed}`,
      for: ["mentor", "mentee", "admin", "event_manager", "volunteer", "hta"],
      mentorStatus: "completed",
      menteeStatus: "completed",
      status: programActionStatus.completed,
    },
    {
      name: (
        <MuiTooltipWrapper
          title={`${
            is_event ? "Events" : `${itemTitlePlural}`
          } that were cancelled/discontiuned before completion`}
        >
          {`Cancelled ${is_event ? "Events" : `${itemTitlePlural}`}`}
        </MuiTooltipWrapper>
      ),
      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).cancelled}`,
      for: ["mentee", "mentor", "admin", "event_manager", "volunteer", "hta"],
      mentorStatus: "cancelled",
      menteeStatus: "cancelled",
      status: programActionStatus.cancelled,
    },
    {
      name: (
        <MuiTooltipWrapper
          title={`${
            is_event ? "Events" : `${itemTitlePlural}`
          } that have been archived`}
        >
          {`Archived ${is_event ? "Events" : `${itemTitlePlural}`}`}
        </MuiTooltipWrapper>
      ),
      count: 0,
      page: `${pipeUrl}${programFilterUrls(is_event).archived}`,
      for: ["admin"],
      mentorStatus: "archived",
      menteeStatus: "archived",
      status: programActionStatus.archived,
    },
  ];
};

export const RequestStatusArray = [
  {
    key: "program_request",
    name: "Program Requests",
    menteeTabName: "Mentees Program Requests",
  },
  {
    key: "mentee_program_request",
    name: `${MenteesType} Program Requests`,
    menteeTabName: "Mentees Program Requests",
  },
  {
    key: "member_join_request",
    name: "Request Approval",
    tooltip:
      "Review and approve or reject requests from users wanting to join the platform",
  },
  {
    key: "goal_request",
    name: "Goal Requests",
  },
  {
    key: "resource_access_request",
    name: "Resource Access Requests",
  },
  {
    key: "technical_support_request",
    name: "Technical Support Requests",
  },
  {
    key: "testimonial_request",
    name: "Testimonial Requests",
  },
  {
    key: "certificate_request",
    name: "Certificate Requests",
  },
  {
    key: "report_request",
    name: (
      <MuiTooltipWrapper title="Insights awaiting your input or action">
        <span>Insight Requests</span>
      </MuiTooltipWrapper>
    ),
  },
  {
    key: "learning_access_requests",
    name: "Learning Access Requests",
  },
  {
    key: "new_goals_request",
    name: "Goal Requests",
  },
  {
    key: "mentees_joining_request",
    name: `${MenteesType} Join Requests`,
  },
];

export const RequestStatus = {
  programRequest: {
    key: "program_request",
    name: "Program Requests",
  },
  menteeprogramRequest: {
    key: "mentee_program_request",
    name: "Program Requests",
  },
  memberJoinRequest: {
    key: "member_join_request",
    name: "Member Join Requests",
  },
  goalRequest: {
    key: "goal_request",
    name: "Goal Requests",
  },
  resourceAccessRequest: {
    key: "resource_access_request",
    name: "Resource Access Requests",
  },
  technicalSupportRequest: {
    key: "technical_support_request",
    name: "Technical Support Requests",
  },
  testimonicalRequest: {
    key: "testimonial_request",
    name: "Testimonial Requests",
  },
  certificateRequest: {
    key: "certificate_request",
    name: "Certificate Requests",
  },
  reportRequest: {
    key: "report_request",
    name: "Insight Requests",
  },
  learningAccessRequests: {
    key: "learning_access_requests",
    name: "Learning Access Requests",
  },
  newGoalsRequests: {
    key: "new_goals_request",
    name: "Goal Requests",
  },
  extendedRequests: {
    key: "extended_request",
    name: "Extension Requests",
  },
  reOpenRequests: {
    key: "re_open_request",
    name: "Re-open Requests",
  },
  mentorChangeRequest: {
    key: "mentor_change_request",
    name: "Mentor Change Requests",
  },
  menteesJoinRequest: {
    key: "mentees_joining_request",
    name: `${MenteesType} Joining Requests`,
  },
};

export const myRequestOverview = [
  {
    name: "Program Requests",
    key: RequestStatus.programRequest.key,
    count: 0,
    status: RequestStatus.programRequest.key,
    for: ["admin", "mentor", "mentee", "event_manager", "volunteer"],
    forTabs: "my",
  },
  {
    name: (
      <MuiTooltipWrapper title="Review and approve or reject requests from users wanting to join the platform">
        <span>Request Approval</span>
      </MuiTooltipWrapper>
    ),
    key: RequestStatus.memberJoinRequest,
    count: 0,
    status: RequestStatus.memberJoinRequest.key,
    for: ["admin"],
    forTabs: "my",
  },

  // {
  //   name:'Mentees Join Requests',
  //   key:RequestStatus.menteesJoinRequest.key,
  //   count:0,
  //   status:RequestStatus.menteesJoinRequest.key,
  //   for:["admin"],
  //   forTabs:"mentees"
  // },
  // {
  //   name: "Mentees Program Requests",
  //   key: RequestStatus.menteeprogramRequest.key,
  //   count: 0,
  //   status: RequestStatus.menteeprogramRequest.key,
  //   for: ["mentor", "admin"],
  //    forTabs:"mentees"
  // },
  // {
  //   name: 'Resource Access Requests',
  //   key: RequestStatus.resourceAccessRequest,
  //   count: 0,
  //   status: RequestStatus.resourceAccessRequest.key,
  //   for: ['admin', 'mentor'],
  // },
  // {
  //   name: "Goal Requests",
  //   key: RequestStatus.newGoalsRequests.key,
  //   count: 0,
  //   status: RequestStatus.newGoalsRequests.key,
  //   for: ["mentee", "mentor"],
  // },
  // {
  //   name: "Certificate Requests",
  //   key: RequestStatus.certificateRequest,
  //   count: 0,
  //   status: RequestStatus.certificateRequest.key,
  //   for: ["admin", "mentor"],
  //    forTabs:"my"
  // },
  {
    name: "Insights Requests",
    key: RequestStatus.reportRequest,
    count: 0,
    status: RequestStatus.reportRequest.key,
    for: ["admin", "mentor", "event_manager"],
    forTabs: "my",
  },

  // {
  //   name: "Testimonial Requests",
  //   key: RequestStatus.testimonicalRequest,
  //   count: 0,
  //   status: RequestStatus.testimonicalRequest.key,
  //   for: ["admin", "mentor"],
  // },
  // {
  //   name: 'Technical Support Requests',
  //   key: RequestStatus.technicalSupportRequest,
  //   count: 0,
  //   status: RequestStatus.technicalSupportRequest.key,
  //   for: ['admin'],
  // },
  // {
  //   name: "Goal Requests",
  //   key: RequestStatus.goalRequest,
  //   count: 0,
  //   status: RequestStatus.goalRequest.key,
  //   for: ["admin"],
  // },
  // {
  //   name: 'Extension Requests',
  //   key: RequestStatus.extendedRequests,
  //   count: 0,
  //   status: RequestStatus.extendedRequests.key,
  //   for: ['admin', 'mentor'],
  // },
  // {
  //   name: 'Re-open Requests',
  //   key: RequestStatus.reOpenRequests,
  //   count: 0,
  //   status: RequestStatus.reOpenRequests.key,
  //   for: ['mentor'],
  // },
  // {
  //   name: "Learning Access Requests",
  //   key: RequestStatus.learningAccessRequests.key,
  //   count: 0,
  //   status: RequestStatus.learningAccessRequests.key,
  //   for: ["mentee"],
  //    forTabs:"my"
  // },
];

export const menteesRequestOverview = [
  {
    name: `${MenteesType} Program Requests`,
    key: RequestStatus.programRequest.key,
    count: 0,
    status: RequestStatus.programRequest.key,
    for: ["mentor", "admin", "event_manager"],
    forTabs: "mentees",
  },
  {
    name: `${MenteesType} Joining Requests`,
    key: RequestStatus.memberJoinRequest.key,
    count: 0,
    status: RequestStatus.memberJoinRequest.key,
    for: ["admin"],
  },
  // {
  //   name: 'Resource Access Requests',
  //   key: RequestStatus.resourceAccessRequest,
  //   count: 0,
  //   status: RequestStatus.resourceAccessRequest.key,
  //   for: ['mentor', 'admin'],
  // },
  // {
  //   name: "Goal Requests",
  //   key: RequestStatus.goalRequest,
  //   count: 0,
  //   status: RequestStatus.goalRequest.key,
  //   for: ["admin"],
  // },
];

export const adminRequestOverview = [
  {
    name: `${MentorType} Change Requests`,
    key: RequestStatus.mentorChangeRequest.key,
    count: 0,
    status: RequestStatus.mentorChangeRequest.key,
    for: ["mentor", "event_manager"],
  },
  {
    name: "Testimonial Requests",
    key: RequestStatus.testimonicalRequest,
    count: 0,
    status: RequestStatus.testimonicalRequest.key,
    for: ["admin"],
  },
];

export const allowedImagesTypes = ["png", "jpeg", "jpg"];

export const allowedDocTypes = ["pdf", "doc", "docx", "txt"];

export const allowedVideoTypes = ["avi", "mp4", "mov"];

export const TaskFileTypes = [
  ...allowedImagesTypes,
  ...allowedDocTypes,
  ...allowedVideoTypes,
];

export const TaskAllStatus = {
  yettostart: "yettostart",
  start: "start",
  pending: "pending",
  submitted: "submitted",
  completed: "completed",
  rejected: "rejected",
  draft: "draft",
  newtask: "newtask",
  inprogress: "inprogress",
  waiting_for_approval: "waiting_for_approval",
};

export const TaskStatus = {
  newtask: "New",
  yettostart: "Start",
  rejected: "Cancelled",
  start: "In-Progress",
  inprogress: "In-Progress",
  pending: "Pending",
  completed: "Completed",
  submitted: "Submitted",
  cancelled: "Cancelled",
  draft: "Draft",
  waiting_for_approval: "Waiting for Approval",
  reassigned: "Re Assigned",
};

export const TaskApiStatus = {
  update: "update",
  create: "create",
  load: "loaded",
  updatemark: "updatemark",
};

export const ProgramStatusInCard = {
  inprogress: {
    text: "Ongoing",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  yettojoin: {
    text: "Start",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  yettoapprove: {
    text: "Waiting for Approval",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },

  yettostart: {
    text: "Ongoing",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  started: {
    text: "Ongoing",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  assigned: {
    text: "Ongoing",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  paused: {
    text: "Ongoing",
    color: "#FF8A00",
    bg: "#FFE3C2",
  },
  completed: {
    text: "Completed",
    color: "rgb(0, 128, 0)",
    bg: "rgb(235, 255, 243)",
  },
  cancelled: {
    text: "Cancelled",
    color: "rgb(192, 0, 0)",
    bg: "rgb(255, 231, 231)",
  },
  new_program_request_rejected: {
    text: "Rejected",
    color: "rgb(224, 56, 45)",
    bg: "rgb(255, 231, 231)",
  },
};

export const programApprovalStage = {
  yettojoin: {
    status: "yettojoin",
    text: `Waiting for ${AdminType} Approval`,
    type: "waiting",
  },
  yettoapprove: {
    status: "yettoapprove",
    text: `Waiting for ${AdminType} Approval`,
    type: "waiting",
  },
  new_program_request_rejected: {
    status: "new_program_request_rejected",
    text: `Program Request Rejected by ${AdminType}`,
    type: "reject",
  },
  join_request_submitted: {
    status: "join_request_submitted",
    text: `Waiting for Join Request ${AdminType} Approval`,
    type: "waiting",
  },
  join_request_rejected: {
    status: "join_request_rejected",
    text: `Join Request Rejected by ${AdminType}`,
    type: "reject",
  },
  start_request_submitted: {
    status: "start_request_submitted",
    text: `Waiting for Start Request ${AdminType} Approval`,
    type: "waiting",
  },
  start_request_rejected: {
    status: "start_request_rejected",
    text: `Start Request Rejected by ${AdminType}`,
    type: "reject",
  },
  schedule_request_submitted: {
    status: "schedule_request_submitted",
    text: `Waiting for Schedule Request ${AdminType} Approval`,
    type: "waiting",
  },
  cancel_request_submitted: {
    status: "cancel_request_submitted",
    text: `Waiting for Cancel Request ${AdminType} Approval`,
    type: "waiting",
  },
};

export const programWaitingActiveApproval = ["yettoapprove"];

export const programAdminRejected = [
  "new_program_request_rejected",
  "join_request_rejected",
  "start_request_rejected",
];

export const programNotLaunched = ["yettojoin"];

export const programRequestApproval = [
  "join_request_submitted",
  "start_request_submitted",
  "schedule_request_submitted",
  "cancel_request_submitted",
];

export const programNotStarted = [
  "yettoassigned",
  "assigned",
  "yettostart",
  "started",
  "start",
];

export const programInProgress = ["inprogress"];

export const programPaused = ["paused"];

export const programCompleted = ["completed"];

export const programCancelled = ["cancelled"];

export const programNotReady = ["yettoapprove", "draft"];

export const menteeNotJoinCondition = [
  ...programNotReady,
  ...programCancelled,
  ...programCompleted,
  programActionStatus.yettojoin,
];

export const menteeProgramStatus = {
  program_join_request_submitted: {
    status: "program_join_request_submitted",
    text: "Waiting for Approval",
    type: "waiting",
    bgColor: "bg-gray-400",
    txtColor: "text-orange-500",
    icon: <AccessTimeIcon />,
  },
  program_prerequisites_submitted: {
    status: "program_prerequisites_submitted",
    text: "Waiting for Approval",
    type: "waiting",
    bgColor: "bg-gray-400",
    txtColor: "text-orange-500",
    icon: <AccessTimeIcon />,
  },
  program_join_request_rejected: {
    status: "program_join_request_rejected",
    text: "Join request rejected",
    type: "reject",
    bgColor: "bg-red-500",
    txtColor: "text-red-500",
  },
  // program_join_request_accepted: {
  //   status: "program_join_request_accepted",
  //   text: "Join request accepted",
  //   type: "success",
  //   bgColor: "bg-green-500",
  //   txtColor: "text-green-500",
  // },
};

export function capitalizeEachWord(string) {
  return string
    .split(" ") // Split the string into an array of words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    .join(" "); // Join the words back into a single string
}

export const MentorMenteeProfileViewList = (hide) => {
  return [
    {
      label: "First Name",
      value: "first_name",
      grid: 4,
    },
    {
      label: "Last Name",
      value: "last_name",
      grid: 4,
    },
    {
      label: "Primary Phone Number",
      value: "phone_number",
      grid: 4,
    },
    {
      label: "Secondary Contact Number",
      value: "secondary_phone_number",
      grid: 4,
    },
    {
      label: "Mail Id",
      value: "email",
      grid: 4,
    },
    {
      label: "Location",
      value: "location",
      grid: 4,
    },
    {
      label: "Skils",
      value: "strongest_skills",
      grid: 4,
    },
    {
      label: "Professional Bio",
      value: "Professional_Bio",
      grid: 12,
    },
    {
      label: "Documents",
      value: "documents",
      grid: 12,
      hide: hide,
    },
  ];
};

export const followBtnText = {
  new: "Follow",
  waiting: "Requested",
  accepted: "Unfollow",
};

export const taskStatus = {
  completed: "Completed",
  rejected: "Cancel",
  pending: "Pending",
  new: "New",
  cancel: "Cancel",
  inprogress: "In Progress",
  reassigned: "Re Assigned",
};

export const taskStatusColorNew = {
  completed: {
    bg: "#EBFFF3",
    color: "#16B681",
  },
  rejected: {
    bg: "#FFE7E7",
    color: "#E0382D",
  },
  pending: {
    bg: "#FFFBE9", // still light yellowish background
    color: "#8A6D00", // dark golden/yellow-brown text
  },
  new: {
    bg: "#EEF5FF",
    color: "#1D5BBF",
  },
  cancel: {
    bg: "#FFE7E7",
    color: "#E0382D",
  },
  inprogress: {
    bg: "#FFFBE9",
    color: "#FFD500",
  },
  reassigned: {
    bg: "#EEF5FF",
    color: "#1D5BBF",
  },
};

export const view = {
  viewOnly: "viewOnly",
};

export function getCurrentWeekAndDay(date = new Date()) {
  const currentDayOfMonth = date.getDate();
  const currentDayOfWeek = date.getDay();

  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const dayOfWeekStart = startOfMonth.getDay();

  const adjustedDate = currentDayOfMonth + dayOfWeekStart;

  const weekNumber = Math.ceil(adjustedDate / 7);

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const currentDayName = daysOfWeek[currentDayOfWeek];

  return { weekNumber, currentDayName };
}

export const user = {
  mentor: "mentor",
  mentee: "mentee",
  admin: "admin",
  super_admin: "super_admin",
  event_manager: "event_manager",
  volunteer: "volunteer",
  make_a_request: "make_a_request",
  provider: 'provider',
  hta: 'hta',
  medical_assistant: 'medical_assistant',
  healthcare_transportation_associate: 'healthcare_transportation_associate',
  volunteer_photographer: "volunteer_photographer",
};

export const tenants = {
  VMS: "vms",
  MMA: "mma",
  LCA: "lca",
  TIM: "tim",
  BPS: "bps",
};

export const equipmentStatusText = {
  inuse: "In Use",
  available: "Available",
  maintenance: "Maintenance",
  archive: "Archive",
  deactivated: "Deactivated",
  archived: "Archive",
};

export const equipmentStatusColor = {
  inuse: {
    color: "#FE634E",
    bg: "#FFF1E7",
  },
  available: {
    color: "#16B681",
    bg: "#EBFFF3",
  },
  maintenance: {
    color: "#FF7600",
    bg: "#FFF4E5",
  },
  deactivated: {
    color: "#BF453A",
    bg: "#FFE7E7",
  },
  archived: {
    color: "#4F4F4F",
    bg: "#DFDFDF",
  },
};

export const selectedTheme = localStorage.getItem("theme");

export const dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(","),
    mime = arr[0].match(base64CheckString)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const imageUrlToBase64 = async (url) => {
  if (url?.length) {
    return await fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }
};
const clearSession = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem(AUTH_PROVIDER_KEY);
  localStorage.removeItem(MICROSOFT_SESSION_USER_KEY);
  localStorage.removeItem(AZURE_SSO_SESSION_KEY);
  sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
  sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);
  localStorage.setItem("redirect_logout", "true");
};

export const logout = async ({ userAccountLogout, dispatch, navigate, subscribedTopics = [], unsubscribe = () =>false }) => {
  const hasAccessToken = !!localStorage.getItem("access_token");
  const hasRefreshToken = !!localStorage.getItem("refresh_token");
  const microsoftSession = isMicrosoftSession();

  try {
    if (!microsoftSession && hasAccessToken && hasRefreshToken) {
      await userAccountLogout().unwrap();
    }
  } catch (err) {
    // toast.error(err?.data?.message || "Server logout failed");
  } finally {
    // Always run this part
    clearSession();
    dispatch({ type: "logout" });
    if(subscribedTopics?.length){
      unsubscribe(subscribedTopics)
    }
    if (microsoftSession) {
      try {
        const redirected = await logoutMicrosoft();
        if (redirected) {
          return;
        }
      } catch (error) {
        console.error("Microsoft logout failed", error);
      }
    }
    navigate("/login");
  }
};

export const termsConditions = `Terms and Conditions`;

export const privacyPolicy = `Privacy Policy`;

export const randomNumber = () => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

export const transformDataForChart = (data) => {
  if (!data?.data) return [];

  return data?.data?.map((item, index) => ({
    name: item.label,
    value: item.count,
    // color: ["#FE634E", "#4F46E5", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6"][
    //   index % 6
    // ],
  }));
};

 export const convertBase64ToFile = (base64String, fileName) => {
    const arr = base64String.split(",");
    const mime = arr[0].match(base64CheckString)[1]; // Extract MIME type
    const byteString = atob(arr[1]); // Decode Base64

    let n = byteString.length;
    let uint8Array = new Uint8Array(n);
    while (n--) {
      uint8Array[n] = byteString.charCodeAt(n);
    }

    const file = new File([uint8Array], fileName, { type: mime });
    return file;
  };

export const generateGridValues = (data) => {
  if (!data?.data?.length) return [0, 5, 10, 15, 20, 25];

  const maxValue = Math.max(...data.data.map((item) => item.count));
  const step = Math.ceil(maxValue / 5);
  return [0, ...Array.from({ length: 5 }, (_, i) => (i + 1) * step)];
};

export const emailRegexString =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const colors = [
  "#6D6E8B",
  "#5FC4C4",
  "#686464",
  "#B8B3FF",
  "#FFD700",
  "#63CD81",
  "#7963CD",
  "#5BC7E6",
  "#FF8F78",
];

export const getColorArray = (size) =>
  Array.from({ length: size }, (_, i) => colors[i % colors.length]);

export const base64CheckString = /:[^:;\n]{1,100};/;

const countryCodeMap = {
  "Asia/Kolkata": "+91",
  "Asia/Calcutta": "+91",
  "America/New_York": "+1",
  "America/Los_Angeles": "+1",
};

export function getSystemCountryCode() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return countryCodeMap[tz] || "+1";
  } catch (err) {
    console.error("Error detecting country code:", err);
    return "+1";
  }
}
